import { useRef } from "react";
import { Body, Cell, Header, Option, Select, Table, Row, showToast } from "@jobber/components";
import { Button } from "@jobber/components/Button";
import { trpc } from "@/services/trpc";
import CreateTimeWindowModal, { RefFields as CreateTimeWindowModalRefFields, TimeWindowFormResult } from "@/components/CreateTimeWindowModal";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import type { RefFields as ConfirmRefFields } from "@/components/ConfirmationDialog";
import { ServiceAreaUseQueryType } from "@/services/trpc/types";

export default function GeneralSettingsTab () {
  const confirmationDialogRef = useRef<ConfirmRefFields>(null)

  const servicesQuery = trpc.services.list.useQuery() as ServiceAreaUseQueryType
  const defaultServiceQuery = trpc.settings.getDefaultService.useQuery()
  const defaultServiceMutation = trpc.settings.updateDefaultService.useMutation()

  const timeWindowsQuery = trpc.settings.getTimeWindows.useQuery()
  const timeWindowsMutation = trpc.settings.updateTimeWindows.useMutation()
  const seedTimeWindowsMutation = trpc.settings.seedTimeWindows.useMutation()

  const createTimeWindowModalRef = useRef<CreateTimeWindowModalRefFields>(null)

  const onSeedTimeWindowsClick = async () => {
    seedTimeWindowsMutation.mutate(undefined, {
      onSuccess: () => {
        showToast({
          message: 'Time windows seeded successfully',
          variation: 'success',
        })
        timeWindowsQuery.refetch()
      },
      onError: (e) => {
        console.log(e.message)
        showToast({
          message: e.message,
          variation: 'error'
        })
      }
    })
  }

  const onCreateClick = () => {
    createTimeWindowModalRef?.current?.openModal()
  }
  const onDefaultServiceChange = (value: string) => {
    defaultServiceMutation.mutate(value, {
      onSuccess: () => {
        showToast({
          message: 'Default service successfully changed',
          variation: 'success',
        })
        defaultServiceQuery.refetch()
      }
    })
  }
  const onTimeWindowFormSubmit = async (formData: TimeWindowFormResult) => {
    if (formData.index === undefined || formData.index === null) {
      const payloadData = [...(timeWindowsQuery.data || []), formData]
      timeWindowsMutation.mutate(payloadData, {
        onSuccess: () => {
          showToast({
            message: 'Time window successfully created',
            variation: 'success',
          })
          timeWindowsQuery.refetch()
        }
      })
    } else {
      const index = formData.index
      const newTimeWindow = {...formData}
      delete newTimeWindow.index
      const payloadData = timeWindowsQuery.data?.map((tw: any, i: number) => {
        if (i === index) {
          return newTimeWindow
        } else {
          return tw
        }
      })

      timeWindowsMutation.mutate(payloadData, {
        onSuccess: () => {
          showToast({
            message: 'Time window successfully updated',
            variation: 'success',
          })
          timeWindowsQuery.refetch()
        }
      })
    }
  }
  const onTimeWindowEditClick = (index: number) => {
    const timeWindow = {
      index,
      ...timeWindowsQuery.data?.[index]
    }
    createTimeWindowModalRef?.current?.openModal(timeWindow)
  }

  const onTimeWindowDelete = async (index: number) => {
    const res = await confirmationDialogRef.current?.open({
      title: `Delete time window ${timeWindowsQuery?.data?.[index].name}`,
      message: 'Are you sure you want to delete this time window?'
    })
    if (res) {
      const newTimeWindows = timeWindowsQuery.data?.filter((tw: any, i: number) => i !== index)
      timeWindowsMutation.mutate(newTimeWindows, {
        onSuccess: () => {
          showToast({
            message: 'Time window successfully deleted',
            variation: 'success',
          })
          timeWindowsQuery.refetch()
        }
      })
    }
  }

  return (
    <div>
      <Select
        placeholder="Select the default service"
        value={defaultServiceQuery?.data || undefined}
        onChange={onDefaultServiceChange}
      >
        {servicesQuery && servicesQuery.data?.map((service: { id: string, name: string }) => (
          <Option key={service.id} value={service.id}>{service.name}</Option>
        ))}
      </Select>
      <div style={{marginTop: '20px'}}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.5em', fontWeight: 'bold', marginBottom: '10px' }}
        >
          <div>
            Time windows
          </div>
          <Button label="Add new" icon={'plus'} onClick={onCreateClick}/>
        </div>
        {timeWindowsQuery?.data?.length ? (<Table>
          <Header>
            <Cell>Name</Cell>
            <Cell>Start time</Cell>
            <Cell>End time</Cell>
            <Cell>Slots</Cell>
            <Cell align="right"></Cell>
          </Header>
          <Body>
            { timeWindowsQuery?.data?.map((tw: any, index: number) => (
              <Row key={index}>
                <Cell>{tw.name}</Cell>
                <Cell>{tw.timeFrom}</Cell>
                <Cell>{tw.timeTo}</Cell>
                <Cell>{tw.slotsPerWindow}</Cell>
                <Cell>
                  <div style={{ display: "flex", justifyContent: "end" }}>
                    <Button
                      type="tertiary"
                      variation="destructive"
                      ariaLabel="delete"
                      icon="trash"
                      onClick={() => onTimeWindowDelete(index)}
                    />
                    <Button
                      type="tertiary"
                      variation="subtle"
                      ariaLabel="delete"
                      icon="edit"
                      onClick={() => onTimeWindowEditClick(index)}
                    />
                </div>
              </Cell>
            </Row>
            ))}
          </Body>
        </Table>) : (
          <div style={{textAlign: 'center'}}>
            <p>Time windows not found.</p>
            <Button
              type="tertiary"
              variation="subtle"
              ariaLabel="Seed time windows"
              icon="task"
              label="Seed time windows"
              onClick={() => onSeedTimeWindowsClick()}
            />
            </div>)}
      </div>
      <CreateTimeWindowModal
        ref={createTimeWindowModalRef}
        onSubmit={onTimeWindowFormSubmit}
      />
      <ConfirmationDialog ref={confirmationDialogRef} />
    </div>
  )
}
